/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "User feature matrix", description = "Build and manage the customer features AI Decisioning uses to make per-user decisions, using the self-serve Feature Matrix Builder or with your Hightouch team.", lastUpdatedDate = new Date(1788220800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "User feature matrix",
        description: "Build and manage the customer features AI Decisioning uses to make per-user decisions, using the self-serve Feature Matrix Builder or with your Hightouch team.",
        lastUpdatedDate: "Tue Sep 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Key concepts",
          "depth": 1,
          "id": "key-concepts"
        }, {
          "value": "Why the UFM matters",
          "depth": 0,
          "id": "why-the-ufm-matters"
        }, {
          "value": "Build a feature matrix",
          "depth": 0,
          "id": "build-a-feature-matrix"
        }, {
          "value": "Step 1: Create a feature matrix",
          "depth": 1,
          "id": "step-1-create-a-feature-matrix"
        }, {
          "value": "Step 2: Select and configure features",
          "depth": 1,
          "id": "step-2-select-and-configure-features"
        }, {
          "value": "Step 3: Preview",
          "depth": 1,
          "id": "step-3-preview"
        }, {
          "value": "Step 4: Save and attach to an agent",
          "depth": 1,
          "id": "step-4-save-and-attach-to-an-agent"
        }, {
          "value": "Share a feature matrix across agents",
          "depth": 0,
          "id": "share-a-feature-matrix-across-agents"
        }, {
          "value": "Manage schema changes",
          "depth": 0,
          "id": "manage-schema-changes"
        }, {
          "value": "Recommended order of operations for schema changes",
          "depth": 1,
          "id": "recommended-order-of-operations-for-schema-changes"
        }, {
          "value": "Choose high-signal features",
          "depth": 0,
          "id": "choose-high-signal-features"
        }, {
          "value": "Example: E-commerce feature matrix",
          "depth": 1,
          "id": "example-e-commerce-feature-matrix"
        }, {
          "value": "Feature engineering best practices",
          "depth": 1,
          "id": "feature-engineering-best-practices"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li",
      img: "img",
      code: "code",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers and data teams configuring AID agents"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["At least one parent model configured in your ", _jsx(_components.a, {
                    href: "/customer-studio/schema",
                    children: "Hightouch Schema"
                  })]
                }), _jsx("li", {
                  children: "Related or event models available in your Schema for behavioral data"
                }), _jsxs("li", {
                  children: ["An active ", _jsx(_components.a, {
                    href: "/ai-decisioning/configuration",
                    children: "AID engine"
                  })]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "The user feature matrix (UFM) defines the customer attributes and behavioral signals AID uses to evaluate each user individually and determine which message, offer, or channel is most likely to drive your desired outcome."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "user feature matrix (UFM)"
        }), " is a structured set of customer attributes and behavioral features used by ", _jsx(_components.strong, {
          children: "AI Decisioning (AID)"
        }), " to evaluate what is most likely to drive a desired outcome for each individual user."]
      }), "\n", _jsxs(_components.p, {
        children: ["The UFM is built from data in your ", _jsx(_components.strong, {
          children: "data warehouse"
        }), " and made available to AID through your Hightouch schema. It stays in your warehouse — secure, governed, and under your control."]
      }), "\n", _jsx(_components.h3, {
        id: "key-concepts",
        children: "Key concepts"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Features"
          }), " are customer attributes and behaviors that help predict which actions drive your goals."]
        }), "\n", _jsx(_components.li, {
          children: "High-signal features typically span behavioral history, customer value, demographics, preferences, and current context."
        }), "\n", _jsx(_components.li, {
          children: "More features are better than fewer. AID's models learn which ones matter and discard those that don't."
        }), "\n", _jsx(_components.li, {
          children: "Start with the data you have and enrich over time."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "why-the-ufm-matters",
        children: "Why the UFM matters"
      }), "\n", _jsx(_components.p, {
        children: "Without a UFM, AID treats users in broad segments — \"high-value customers,\" \"recent purchasers,\" or \"email engagers.\" With a well-built UFM, AID can make decisions at the individual user level instead."
      }), "\n", _jsx(_components.p, {
        children: "AID uses UFM features to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Understand individual context"
          }), " by considering how multiple features interact (for example, recent behavior, lifecycle stage, and preferences)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Identify patterns"
          }), " that predict outcomes by learning which feature combinations are most likely to drive ", _jsx(_components.a, {
            href: "/ai-decisioning/agents#step-2-add-and-manage-goals",
            children: "goals"
          }), " like clicks, purchases, or conversions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Continuously improve decisions"
          }), " as new outcomes are observed and incorporated into future predictions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Make per-user decisions at scale"
          }), " without manual segmentation or rules."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "build-a-feature-matrix",
        children: "Build a feature matrix"
      }), "\n", _jsx(_components.p, {
        children: "The Feature Matrix Builder is a self-serve interface that lets you create and manage UFMs directly in the Hightouch UI without writing SQL. Work with your Strategy Consultant and Machine Learning Engineer to get enabled, then build and iterate on your feature matrices yourself. If you have custom SQL features defined in a related model or the parent model, you can still reference them through the builder."
      }), "\n", _jsx(_components.p, {
        children: "Before you start, confirm that:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The parent model for the agent you're building this matrix for is already configured in your Hightouch Schema."
        }), "\n", _jsx(_components.li, {
          children: "The related models or event models you want to use as features are available in your Schema. If they aren't set up yet, work with your Hightouch team and internal data owner to add them first."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "step-1-create-a-feature-matrix",
        children: "Step 1: Create a feature matrix"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "AI Decisioning > Configuration > Feature Matrix"
        }), " and select ", _jsx(_components.strong, {
          children: "New feature matrix"
        }), ". Name it and select the parent model this matrix will use. The matrix must match the parent model of the agents you assign it to."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/user-feature-matrix/aid-create-feature-matrix.png",
          alt: "Configuration page showing the Feature Matrix tab with an existing matrix and New feature matrix button"
        })
      }), "\n", _jsx(_components.h3, {
        id: "step-2-select-and-configure-features",
        children: "Step 2: Select and configure features"
      }), "\n", _jsx(_components.p, {
        children: "Use the model sidebar to navigate between your parent model and related data sources."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Parent model and one-to-one related model columns"
          }), " are direct user attributes that require no aggregation."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Event models and one-to-many related models"
          }), " have multiple rows per user and require aggregation. Configure the aggregation type and time window for each column."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/user-feature-matrix/aid-select-configure-features.png",
          alt: "Feature Matrix Builder showing source columns from the data warehouse with type, cast, dependency, and insights settings"
        })
      }), "\n", _jsx(_components.p, {
        children: "Each column in the builder has the following settings:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Setting"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "What it controls"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Type"
            }), _jsx(_components.td, {
              children: "The column's data type, detected automatically from your warehouse"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Cast To"
            }), _jsx(_components.td, {
              children: "Override the detected type. Available types: String, Number, Big Integer, Boolean, Timestamp, Date, Unix Timestamp"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Dependency"
            }), _jsxs(_components.td, {
              children: ["When checked, passes the column through for ", _jsx(_components.a, {
                href: "/ai-decisioning/insights",
                children: "insights"
              }), " and analysis without using it as a model input. Use for identifier columns or features you want to analyze against but not train on"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Insights"
            }), _jsxs(_components.td, {
              children: ["When checked, includes the column in AID's ", _jsx(_components.a, {
                href: "/ai-decisioning/insights",
                children: "performance insights"
              }), " to explain why certain messages perform better for certain users. Capped at 50 columns"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Null Handling"
            }), _jsx(_components.td, {
              children: "Controls how missing values are treated during model training"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "For event models and one-to-many related models, additional settings appear:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Setting"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "What it controls"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Aggregation"
            }), _jsx(_components.td, {
              children: "How the model summarizes multiple rows per user (see table below)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Time Windows"
            }), _jsx(_components.td, {
              children: "The lookback period for the aggregation (for example, 7d, 30d, 90d)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Filter"
            }), _jsxs(_components.td, {
              children: ["Narrows the rows included in the aggregation (for example, ", _jsx(_components.code, {
                children: "channel = 'email'"
              }), ")"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Available aggregations:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Aggregation"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "What it does"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Example"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "count"
            }), _jsx(_components.td, {
              children: "Number of occurrences"
            }), _jsx(_components.td, {
              children: "Total purchases"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "sum"
            }), _jsx(_components.td, {
              children: "Total value"
            }), _jsx(_components.td, {
              children: "Total revenue"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "avg"
            }), _jsx(_components.td, {
              children: "Mean value"
            }), _jsx(_components.td, {
              children: "Average order value"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "min / max"
            }), _jsx(_components.td, {
              children: "Extreme values"
            }), _jsx(_components.td, {
              children: "Smallest or largest order"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "mode"
            }), _jsx(_components.td, {
              children: "Most common value"
            }), _jsx(_components.td, {
              children: "Most-used payment method"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "first / last"
            }), _jsx(_components.td, {
              children: "Earliest or most recent"
            }), _jsx(_components.td, {
              children: "First purchase date"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "count distinct"
            }), _jsx(_components.td, {
              children: "Unique value count"
            }), _jsx(_components.td, {
              children: "Unique products purchased"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Each time window you select generates an independent column. For example, selecting ", _jsx(_components.strong, {
          children: "count"
        }), " with ", _jsx(_components.strong, {
          children: "7d"
        }), " and ", _jsx(_components.strong, {
          children: "30d"
        }), " produces ", _jsx(_components.code, {
          children: "purchases_count_7d"
        }), " and ", _jsx(_components.code, {
          children: "purchases_count_30d"
        }), ". Available windows: All Time, 7d, 14d, 21d, 30d, 60d, 90d, 180d, 365d."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Use the ", _jsx(_components.strong, {
            children: "Filter"
          }), " setting on event models to create specialized variants. For\nexample, filter email events by ", _jsx(_components.code, {
            children: "channel = 'email'"
          }), " to get email-specific\ncounts separate from push or SMS."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "step-3-preview",
        children: "Step 3: Preview"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Preview"
        }), " before saving. A preview drawer opens with two tabs: ", _jsx(_components.strong, {
          children: "Features"
        }), " shows every generated output column, and ", _jsx(_components.strong, {
          children: "SQL"
        }), " shows the generated query so you can run it live against your warehouse to verify results. No SQL knowledge is required to use the builder, but the SQL tab is useful if you want to inspect or debug the underlying query."]
      }), "\n", _jsx(_components.h3, {
        id: "step-4-save-and-attach-to-an-agent",
        children: "Step 4: Save and attach to an agent"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Save"
          }), " in the Feature Matrix Builder."]
        }), "\n", _jsxs(_components.li, {
          children: ["Go to ", _jsxs(_components.strong, {
            children: ["AI Decisioning > ", _jsx(_components.a, {
              href: "/ai-decisioning/agents",
              children: "Agents"
            })]
          }), " and select the agent you want to use the matrix with."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the agent's ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab and scroll to the ", _jsx(_components.strong, {
            children: "Feature matrix"
          }), " section."]
        }), "\n", _jsx(_components.li, {
          children: "Select your feature matrix from the dropdown and save."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The agent will use the attached matrix for model training and personalization immediately."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/user-feature-matrix/aid-attach-feature-matrix.png",
          alt: "Agent Configuration tab showing the Feature matrix dropdown for attaching a matrix"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "share-a-feature-matrix-across-agents",
        children: "Share a feature matrix across agents"
      }), "\n", _jsxs(_components.p, {
        children: ["A feature matrix can be attached to any ", _jsx(_components.a, {
          href: "/ai-decisioning/agents",
          children: "agent"
        }), " within the same AID engine. Use a ", _jsx(_components.strong, {
          children: "single shared feature matrix"
        }), " when most agents need the same core view of the user. This keeps setup simple and ensures consistent signals across agents."]
      }), "\n", _jsxs(_components.p, {
        children: ["Create ", _jsx(_components.strong, {
          children: "agent-specific matrices"
        }), " when different use cases need genuinely different user views — for example, an onboarding agent vs. a re-engagement agent. Duplicate the default matrix, tailor the columns, and attach each to its corresponding agent."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The main tradeoff with a large shared matrix is compute: the AID engine\nrecalculates all UFM features daily for every eligible user. If many features\nare irrelevant for some agents, agent-specific matrices are more efficient.\nAID models will discount low-signal features naturally, so a slightly\noversized shared matrix is often fine to start."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "manage-schema-changes",
        children: "Manage schema changes"
      }), "\n", _jsxs(_components.p, {
        children: ["If a column is removed from your warehouse schema but still referenced in the feature matrix, it will appear with a ", _jsx(_components.strong, {
          children: "Removed"
        }), " label and italicized name in the UI. This is intentional — AID surfaces the error rather than silently dropping the column."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/user-feature-matrix/aid-removed-column.png",
          alt: "Feature matrix column showing a Removed label for a column no longer in the warehouse schema"
        })
      }), "\n", _jsx(_components.p, {
        children: "If a removed column is left selected, the scheduled run that generates messages for your agents will fail. You must either restore the column in your schema or deselect it in the feature matrix before the next run."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "When looking for the column, make sure you are in the correct model. The\ncolumn will only appear under the data source it was originally added from."
        })
      }), "\n", _jsx(_components.h3, {
        id: "recommended-order-of-operations-for-schema-changes",
        children: "Recommended order of operations for schema changes"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Remove the column from the feature matrix and save."
        }), "\n", _jsx(_components.li, {
          children: "Then make the schema change in your warehouse."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If the schema is changed first, AID will flag the error and the run will pause until the feature matrix is updated."
      }), "\n", _jsx(_components.p, {
        children: "When new columns are added to your source data, the builder discovers them automatically the next time you open the matrix editor. Select the new columns and save to start using them."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "choose-high-signal-features",
        children: "Choose high-signal features"
      }), "\n", _jsx(_components.p, {
        children: "The most effective feature matrices span five categories:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Category"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "What it captures"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Examples"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Behavioral history"
            }), _jsx(_components.td, {
              children: "What customers have done"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "days_since_last_purchase"
              }), ", ", _jsx(_components.code, {
                children: "total_purchases_90d"
              }), ", ", _jsx(_components.code, {
                children: "email_click_count_30d"
              }), ", ", _jsx(_components.code, {
                children: "last_date_email_click"
              }), ", ", _jsx(_components.code, {
                children: "app_sessions_last_week"
              }), ", ", _jsx(_components.code, {
                children: "most_engaged_day_of_week"
              }), ", ", _jsx(_components.code, {
                children: "cart_abandonment_count"
              }), ", ", _jsx(_components.code, {
                children: "most_purchased_category"
              }), ", ", _jsx(_components.code, {
                children: "purchase_time_of_day"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Demographic and firmographic"
            }), _jsx(_components.td, {
              children: "Who the customer is"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "age_range"
              }), ", ", _jsx(_components.code, {
                children: "gender"
              }), ", ", _jsx(_components.code, {
                children: "account_type"
              }), ", ", _jsx(_components.code, {
                children: "country"
              }), ", ", _jsx(_components.code, {
                children: "state"
              }), ", ", _jsx(_components.code, {
                children: "timezone"
              }), ", ", _jsx(_components.code, {
                children: "urban_vs_rural"
              }), ", ", _jsx(_components.code, {
                children: "primary_device_type"
              }), ", ", _jsx(_components.code, {
                children: "operating_system"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Customer value and lifecycle"
            }), _jsx(_components.td, {
              children: "Where they are in the journey"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "total_revenue"
              }), ", ", _jsx(_components.code, {
                children: "avg_order_value"
              }), ", ", _jsx(_components.code, {
                children: "months_as_customer"
              }), ", ", _jsx(_components.code, {
                children: "days_since_signup"
              }), ", ", _jsx(_components.code, {
                children: "subscription_tier"
              }), ", ", _jsx(_components.code, {
                children: "months_until_renewal"
              }), ", ", _jsx(_components.code, {
                children: "in_last_month_of_contract"
              }), ", ", _jsx(_components.code, {
                children: "engagement_decline_30d"
              }), ", ", _jsx(_components.code, {
                children: "product_usage_frequency"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Preference and engagement"
            }), _jsx(_components.td, {
              children: "How they respond to outreach"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "email_response_%"
              }), ", ", _jsx(_components.code, {
                children: "push_response_%"
              }), ", ", _jsx(_components.code, {
                children: "email_click_count_30d"
              }), ", ", _jsx(_components.code, {
                children: "push_click_count_30d"
              }), ", ", _jsx(_components.code, {
                children: "preferred_contact_method"
              }), ", ", _jsx(_components.code, {
                children: "discount_redemption_rate"
              }), ", ", _jsx(_components.code, {
                children: "responds_to_urgency"
              }), ", ", _jsx(_components.code, {
                children: "favorite_product_categories"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Current context"
            }), _jsx(_components.td, {
              children: "Their situation right now"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "current_plan"
              }), ", ", _jsx(_components.code, {
                children: "devices_owned"
              }), ", ", _jsx(_components.code, {
                children: "contract_end_date"
              }), ", ", _jsx(_components.code, {
                children: "eligible_for_upgrade"
              }), ", ", _jsx(_components.code, {
                children: "browsed_last_24hrs"
              }), ", ", _jsx(_components.code, {
                children: "recent_search_terms"
              }), ", ", _jsx(_components.code, {
                children: "payment_method_expiring"
              }), ", ", _jsx(_components.code, {
                children: "billing_issues"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Why each category matters:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Behavioral history"
          }), " is typically the strongest predictor of future behavior. These features reveal customer preferences and intent."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Demographic and firmographic"
          }), " features interact powerfully with behavioral data to create nuanced segments, even though they are less predictive on their own."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Customer value and lifecycle"
          }), " features help the model understand loyalty, price sensitivity, and which interventions are appropriate at each stage."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Preference and engagement"
          }), " features directly inform which messages and channels will resonate with each individual."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Current context"
          }), " features enable timely, contextually relevant outreach that meets customers where they are right now."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "example-e-commerce-feature-matrix",
        children: "Example: E-commerce feature matrix"
      }), "\n", _jsx(_components.p, {
        children: "Here's what a practical feature matrix might look like for a retail brand:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Feature category"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Example features"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Purchase history"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "total_orders"
              }), ", ", _jsx(_components.code, {
                children: "avg_order_value"
              }), ", ", _jsx(_components.code, {
                children: "days_since_last_order"
              }), ", ", _jsx(_components.code, {
                children: "returns_rate"
              }), ", ", _jsx(_components.code, {
                children: "favorite_category"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Engagement"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "email_open_rate_30d"
              }), ", ", _jsx(_components.code, {
                children: "cart_abandonment_count"
              }), ", ", _jsx(_components.code, {
                children: "wishlist_items"
              }), ", ", _jsx(_components.code, {
                children: "website_visits_14d"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Customer value"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "lifetime_revenue"
              }), ", ", _jsx(_components.code, {
                children: "months_as_customer"
              }), ", ", _jsx(_components.code, {
                children: "predicted_ltv"
              }), ", ", _jsx(_components.code, {
                children: "loyalty_tier"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Preferences"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "preferred_brand"
              }), ", ", _jsx(_components.code, {
                children: "mobile_vs_desktop"
              }), ", ", _jsx(_components.code, {
                children: "price_sensitivity_score"
              }), ", ", _jsx(_components.code, {
                children: "responds_to_sales"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Current state"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "cart_value"
              }), ", ", _jsx(_components.code, {
                children: "items_in_cart"
              }), ", ", _jsx(_components.code, {
                children: "days_since_cart_update"
              }), ", ", _jsx(_components.code, {
                children: "browsed_categories_today"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "feature-engineering-best-practices",
        children: "Feature engineering best practices"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Start with what you have."
        }), " You don't need perfect data to begin. Start with basic behavioral events (purchases, logins, page views), demographic information from your CRM, and engagement history from your marketing platforms."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Turn raw events into features."
        }), " Raw event logs need to be aggregated into meaningful features. The Feature Matrix Builder handles this automatically when you configure aggregation types and time windows. For example, raw ", _jsx(_components.code, {
          children: "email_click"
        }), " events become ", _jsx(_components.code, {
          children: "email_clicks_last_7_days"
        }), ", ", _jsx(_components.code, {
          children: "email_clicks_last_30_days"
        }), ", and ", _jsx(_components.code, {
          children: "days_since_last_email_click"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "More features are better than fewer."
        }), " AID models can handle hundreds of features and will automatically determine which ones matter most. If a particular feature ends up not predicting customer behavior, the model will learn to ignore it."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/smart-suppression",
            children: "Smart suppression"
          }), " to optimize send volume."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/insights",
            children: "Insights"
          }), " to review agent performance."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/configuration",
            children: "Configuration"
          }), " to configure AI Decisioning settings."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
